import { useQuery, useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase } from './configs';
import type {
  VodProvisioningConfig,
  UpdateVodProvisioningPayload,
} from './vod-provisioning-types';

// ============================================
// Query keys
// ============================================

export const vodProvisioningKeys = {
  config: (serviceId: string) =>
    ['vod', 'provisioning', serviceId] as const,
};

// ============================================
// Queries
// ============================================

/**
 * Fetch the provisioning configuration. The legacy connector tries `/provisioning`
 * first and falls back to a typo path `/provisoning` that some older servers expose.
 * We mirror that fallback here for compatibility.
 */
export function useVodProvisioningConfig(serviceId: string) {
  return useQuery<VodProvisioningConfig>({
    queryKey: vodProvisioningKeys.config(serviceId),
    enabled: !!serviceId,
    queryFn: async () => {
      const base = mediaBase(serviceId, 'vod');
      try {
        const res = await serviceApiV3.get(`${base}/provisioning`);
        return res.data?.data ?? res.data;
      } catch {
        // Fallback to typo path present on some older server versions.
        const res = await serviceApiV3.get(`${base}/provisoning`);
        return res.data?.data ?? res.data;
      }
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useUpdateVodProvisioningConfig(
  serviceId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseMutationOptions<any, Error, UpdateVodProvisioningPayload>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateVodProvisioningPayload) => {
      const base = mediaBase(serviceId, 'vod');
      try {
        const res = await serviceApiV3.put(`${base}/provisioning`, payload);
        return res.data?.data ?? res.data;
      } catch {
        // Fallback to typo path present on some older server versions.
        const res = await serviceApiV3.put(`${base}/provisoning`, payload);
        return res.data?.data ?? res.data;
      }
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: vodProvisioningKeys.config(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}
